import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { User, Trophy, Target, Award, ArrowLeft, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { toast } from "sonner";
import { LinkedInShareButton } from "@/components/LinkedInShare";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Profile() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/profile`, {
        credentials: 'include'
      });
      const data = await response.json();
      setProfile(data);
    } catch (error) {
      toast.error('Помилка завантаження профілю');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const levelProgress = ((profile?.xp % 100) / 100) * 100;
  const levelTitle = 
    profile?.level >= 10 ? 'Master Architect' :
    profile?.level >= 7 ? 'Principal Architect' :
    profile?.level >= 4 ? 'Solution Architect' :
    'Junior Architect';

  return (
    <div className="min-h-screen bg-background">
      <div className="grid-background min-h-screen">
        <nav className="border-b border-border/50 backdrop-blur-sm bg-background/80">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <Button
                variant="ghost"
                onClick={() => navigate('/dashboard')}
                data-testid="back-button"
                className="flex items-center space-x-2"
              >
                <ArrowLeft className="w-5 h-5" />
                <span>Назад</span>
              </Button>
              <h1 className="text-xl font-bold text-foreground">Профіль</h1>
              <div className="w-24"></div>
            </div>
          </div>
        </nav>

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-8"
          >
            <div className="bg-card/50 backdrop-blur-sm border border-white/5 p-8 rounded-2xl">
              <div className="flex flex-col md:flex-row items-center md:items-start space-y-6 md:space-y-0 md:space-x-8">
                <Avatar className="w-32 h-32 border-4 border-primary">
                  <AvatarImage src={profile?.picture} alt={profile?.name} />
                  <AvatarFallback className="text-4xl">
                    {profile?.name?.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                
                <div className="flex-1 text-center md:text-left">
                  <h2 className="text-4xl font-black text-foreground mb-2">{profile?.name}</h2>
                  <p className="text-lg text-muted-foreground mb-4">{profile?.email}</p>
                  <div className="inline-block px-4 py-2 rounded-full bg-gradient-to-r from-primary to-secondary mb-4">
                    <span className="text-white font-bold">{levelTitle}</span>
                  </div>
                  <div className="mt-4">
                    <LinkedInShareButton 
                      title={`${profile?.name} - ${levelTitle}`}
                      summary={`Я досяг рівня ${profile?.level} (${levelTitle}) в "Архітектор Майбутнього"! 🏆 ${profile?.xp} XP, ${profile?.accuracy}% точність. Вивчаю архітектуру ПЗ через геймифікацію!`}
                      variant="outline"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-card/50 backdrop-blur-sm border border-white/5 p-6 rounded-xl"
              >
                <div className="flex items-center space-x-3 mb-4">
                  <Trophy className="w-8 h-8 text-primary" />
                  <h3 className="text-2xl font-bold text-foreground">Рівень {profile?.level}</h3>
                </div>
                <Progress value={levelProgress} className="h-3 mb-2" />
                <div className="flex justify-between text-sm text-muted-foreground">
                  <span>{profile?.xp} XP</span>
                  <span>{100 - (profile?.xp % 100)} XP до наступного рівня</span>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-card/50 backdrop-blur-sm border border-white/5 p-6 rounded-xl"
              >
                <div className="flex items-center space-x-3 mb-4">
                  <Target className="w-8 h-8 text-accent" />
                  <h3 className="text-2xl font-bold text-foreground">Точність</h3>
                </div>
                <div className="text-5xl font-black text-accent mb-2">{profile?.accuracy}%</div>
                <p className="text-sm text-muted-foreground">Правильних відповідей</p>
              </motion.div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-card/50 backdrop-blur-sm border border-white/5 p-6 rounded-xl text-center"
              >
                <TrendingUp className="w-10 h-10 text-primary mx-auto mb-3" />
                <div className="text-3xl font-bold text-foreground mb-1">{profile?.games_played}</div>
                <p className="text-sm text-muted-foreground">Зіграно ігор</p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="bg-card/50 backdrop-blur-sm border border-white/5 p-6 rounded-xl text-center"
              >
                <Award className="w-10 h-10 text-secondary mx-auto mb-3" />
                <div className="text-3xl font-bold text-foreground mb-1">{profile?.achievements_count}</div>
                <p className="text-sm text-muted-foreground">Досягнень</p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="bg-card/50 backdrop-blur-sm border border-white/5 p-6 rounded-xl text-center"
              >
                <Trophy className="w-10 h-10 text-accent mx-auto mb-3" />
                <div className="text-3xl font-bold text-foreground mb-1">{profile?.xp}</div>
                <p className="text-sm text-muted-foreground">Загально XP</p>
              </motion.div>
            </div>

            <div className="bg-card/50 backdrop-blur-sm border border-white/5 p-8 rounded-2xl">
              <h3 className="text-2xl font-bold text-foreground mb-6">Шлях розвитку</h3>
              <div className="space-y-4">
                {[
                  { level: 1, title: 'Junior Architect', xp: 0, unlocked: true },
                  { level: 4, title: 'Solution Architect', xp: 900, unlocked: profile?.level >= 4 },
                  { level: 7, title: 'Principal Architect', xp: 3600, unlocked: profile?.level >= 7 },
                  { level: 10, title: 'Master Architect', xp: 8100, unlocked: profile?.level >= 10 }
                ].map((milestone, idx) => (
                  <div
                    key={idx}
                    className={`flex items-center space-x-4 p-4 rounded-xl border ${
                      milestone.unlocked
                        ? 'border-primary/50 bg-primary/10'
                        : 'border-border/50 bg-card/30'
                    }`}
                  >
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      milestone.unlocked
                        ? 'bg-gradient-to-br from-primary to-secondary'
                        : 'bg-muted'
                    }`}>
                      <span className="text-white font-bold">{milestone.level}</span>
                    </div>
                    <div className="flex-1">
                      <h4 className="font-bold text-foreground">{milestone.title}</h4>
                      <p className="text-sm text-muted-foreground">{milestone.xp} XP</p>
                    </div>
                    {milestone.unlocked && (
                      <Trophy className="w-6 h-6 text-primary" />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}